// Два справочника в настройках: тип оформления и тип объекта (тз 5.1 и 5.2).
//
// ## Зачем экран
//
// Оба справочника были доступны ТОЛЬКО НА ЧТЕНИЕ и в поставке пусты. Следствия расходились
// далеко от самих справочников:
//
// * тз 5.1 обещает реестру людей ДВА сохранённых представления по типу оформления со
//   счётчиками — с пустым справочником их не могло быть ни одного, и сценарий приёмки 8 на
//   чистом стенде показать было нечем;
// * тз 5.2 обещает, что объект наследует шаблон ведомости от своего типа — то есть состав
//   денежных колонок; с пустым справочником наследовать было не от чего, и каждому объекту
//   шаблон пришлось бы назначать вручную.
//
// Найдено аудитом 06.09.2026.
//
// ## Почему тип оформления заводит заказчик, а не мы
//
// Тз 5.1 дословно: «справочник, состав заполняет Заказчик», и там же — «правовые основания
// оформления определяет Заказчик». Вписать за него «штат» и «договор» в поставку значило бы
// решить за него юридический вопрос и потом увидеть наши слова в его отчётах. Типы объекта
// засеяны (охраняемый объект и аппарат управления — оба взяты из его же файлов), типы
// оформления — нет.
//
// ## Почему удаления нет
//
// Тип уже проставлен живым карточкам, и удаление порвало бы их. Отключение убирает тип из
// выбора, оставляя его тем, у кого он стоит: у них в карточке останется правда, а не пустота.
//
// ## Почему переименование безопасно
//
// Сервер принимал новое название с первого дня, а экран до 15.09.2026 давал только «Отключить»:
// опечатка в названии типа жила в карточке каждого человека и каждого объекта этого типа, и
// исправить её было нечем. Переименование при этом НЕ трогает машинный ключ — по ключу живут
// сохранённые представления реестра, и ссылка «реестр по типу штат» переживает правку
// названия, — и не трогает шаблон ведомости, то есть состав денежных колонок.

import { useState } from 'react';

import { RiBriefcaseLine, RiBuilding4Line } from '@remixicon/react';

import type { EmploymentTypeOut, SiteTypeOut } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import {
  useEmploymentTypesQuery,
  usePayrollTemplatesQuery,
  useSiteTypesQuery,
} from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { useSaveEmploymentType, useSaveSiteType } from './queries';

/** Оба справочника меняют то, как считаются деньги и кто что видит: право администратора. */
const ПРАВО_ПРАВИТЬ = 'core.dictionary.manage';

export function СправочникТиповОформления() {
  const типы = useEmploymentTypesQuery();
  const права = useAuthStore((состояние) => состояние.permissions);
  const можно = can(права, ПРАВО_ПРАВИТЬ);
  const [заводим, setЗаводим] = useState(false);

  return (
    <Block
      title='Типы оформления'
      note='По ним реестр людей делится на два представления со счётчиками'
      action={
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => setЗаводим((было) => !было)}
        >
          {заводим ? 'Не заводить оформление' : 'Завести тип оформления'}
        </Button.Root>
      }
    >
      {заводим && <НовыйТипОформления можно={можно} onГотово={() => setЗаводим(false)} />}

      <ScreenState
        data={типы.data}
        error={типы.error}
        subject='типы оформления'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void типы.refetch()}
        loading={<Skeleton className='h-20 w-full' />}
        empty={
          <EmptyState
            icon={RiBriefcaseLine}
            title='Типов оформления нет'
            description='Их состав заполняете вы: правовые основания оформления мы за вас не выбираем. Пока справочник пуст, реестр людей не делится по оформлению и человека в карточке не к чему отнести.'
          />
        }
      >
        {(данные) => (
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {данные.items.map((тип) => (
              <СтрокаОформления key={тип.id} тип={тип} можно={можно} />
            ))}
          </ul>
        )}
      </ScreenState>
    </Block>
  );
}

function НовыйТипОформления({ можно, onГотово }: { можно: boolean; onГотово: () => void }) {
  const сохранить = useSaveEmploymentType();
  const [название, setНазвание] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <form
      className='flex flex-wrap items-end gap-2 border-b border-stroke-soft-200 py-3'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        if (!можно) {
          setЗапрет(
            'Справочники заводит администратор: от типа оформления зависит, кто увидит человека в реестре.',
          );
          return;
        }
        if (название.trim() === '') {
          setЗапрет('Название обязательно: его человек видит в карточке и в реестре.');
          return;
        }
        setЗапрет(null);
        // Машинный ключ выводит сервер: латиница в поле у людей, которые её не пишут, даёт
        // «shtat1», «shtat2», «shtat_new» — ту же кашу, что была со спецодеждой.
        сохранить.mutate(
          { body: { title: название.trim() } },
          {
            onSuccess: () => {
              setНазвание('');
              onГотово();
            },
          },
        );
      }}
    >
      <div className='min-w-56 flex-1'>
        <TextField
          id='employment-type-title'
          label='Название'
          value={название}
          onChange={setНазвание}
          placeholder='Например: по трудовому договору'
        />
      </div>
      <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
        {сохранить.isPending ? 'Заводим…' : 'Завести оформление'}
      </Button.Root>
      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
    </form>
  );
}

function СтрокаОформления({ тип, можно }: { тип: EmploymentTypeOut; можно: boolean }) {
  const сохранить = useSaveEmploymentType();
  const [название, setНазвание] = useState(тип.title);
  const [правим, setПравим] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <li className='flex flex-wrap items-center justify-between gap-3 py-2.5'>
      {правим ? (
        // Переименование НЕ трогает машинный ключ: по ключу живут сохранённые представления
        // реестра («реестр по типу штат»), и ссылка коллеги обязана пережить правку названия.
        // Ровно поэтому переименование безопасно, а до 15.09.2026 его всё равно не было:
        // сервер принимал название с первого дня, экран давал только «Отключить».
        <div className='flex min-w-0 flex-1 flex-wrap items-end gap-2'>
          <div className='min-w-56 flex-1'>
            <TextField
              id={`employment-type-title-${тип.id}`}
              label='Название'
              value={название}
              onChange={setНазвание}
              hint='Его человек видит в карточке и в реестре. Ссылки на реестр по этому типу переименование переживут.'
            />
          </div>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => {
              if (!можно) {
                setЗапрет('Переименовывать типы может администратор.');
                return;
              }
              if (название.trim() === '') {
                setЗапрет('Название обязательно: его человек видит в карточке и в реестре.');
                return;
              }
              setЗапрет(null);
              сохранить.mutate(
                { id: тип.id, body: { title: название.trim() } },
                { onSuccess: () => setПравим(false) },
              );
            }}
          >
            {сохранить.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              setНазвание(тип.title);
              setЗапрет(null);
              setПравим(false);
            }}
          >
            Отмена
          </Button.Root>
        </div>
      ) : (
        <>
          <span className='min-w-0 text-paragraph-sm text-text-strong-950'>
            {тип.title}
            {!тип.is_active && <span className='text-text-sub-600'> · отключён</span>}
          </span>
          <div className='flex items-center gap-2'>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              onClick={() => setПравим(true)}
            >
              Переименовать
            </Button.Root>
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => {
                if (!можно) {
                  setЗапрет('Отключать типы может администратор.');
                  return;
                }
                setЗапрет(null);
                сохранить.mutate({ id: тип.id, body: { is_active: !тип.is_active } });
              }}
            >
              {тип.is_active ? 'Отключить' : 'Включить'}
            </Button.Root>
          </div>
        </>
      )}
      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
    </li>
  );
}

export function СправочникТиповОбъектов() {
  const типы = useSiteTypesQuery();
  const шаблоны = usePayrollTemplatesQuery();
  const права = useAuthStore((состояние) => состояние.permissions);
  const можно = can(права, ПРАВО_ПРАВИТЬ);
  const [заводим, setЗаводим] = useState(false);

  const названиеШаблона = (id: string | null) =>
    (шаблоны.data?.items ?? []).find((шаблон) => шаблон.id === id)?.title ?? null;

  return (
    <Block
      title='Типы объектов'
      note='От типа объект наследует шаблон ведомости: набор денежных колонок'
      action={
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => setЗаводим((было) => !было)}
        >
          {заводим ? 'Не заводить объект' : 'Завести тип объекта'}
        </Button.Root>
      }
    >
      {заводим && <НовыйТипОбъекта можно={можно} onГотово={() => setЗаводим(false)} />}

      <ScreenState
        data={типы.data}
        error={типы.error}
        subject='типы объектов'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void типы.refetch()}
        loading={<Skeleton className='h-20 w-full' />}
        empty={
          <EmptyState
            icon={RiBuilding4Line}
            title='Типов объектов нет'
            description='Пока их нет, каждому объекту шаблон ведомости придётся назначать отдельно: наследовать его неоткуда.'
          />
        }
      >
        {(данные) => (
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {данные.items.map((тип) => (
              <СтрокаОбъекта
                key={тип.id}
                тип={тип}
                можно={можно}
                шаблон={названиеШаблона(тип.payroll_template_id)}
              />
            ))}
          </ul>
        )}
      </ScreenState>
    </Block>
  );
}

function НовыйТипОбъекта({ можно, onГотово }: { можно: boolean; onГотово: () => void }) {
  const сохранить = useSaveSiteType();
  const шаблоны = usePayrollTemplatesQuery();
  const [название, setНазвание] = useState('');
  const [шаблон, setШаблон] = useState(NO_VALUE);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <form
      className='flex flex-wrap items-end gap-2 border-b border-stroke-soft-200 py-3'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        if (!можно) {
          setЗапрет('Справочники заводит администратор: от типа объекта зависит состав ведомости.');
          return;
        }
        if (название.trim() === '') {
          setЗапрет('Название обязательно: по нему тип выбирают в карточке объекта.');
          return;
        }
        setЗапрет(null);
        сохранить.mutate(
          {
            body: {
              title: название.trim(),
              payroll_template_id: шаблон === NO_VALUE ? null : шаблон,
            },
          },
          {
            onSuccess: () => {
              setНазвание('');
              setШаблон(NO_VALUE);
              onГотово();
            },
          },
        );
      }}
    >
      <div className='min-w-56 flex-1'>
        <TextField
          id='site-type-title'
          label='Название'
          value={название}
          onChange={setНазвание}
          placeholder='Например: вахтовый посёлок'
        />
      </div>
      <div className='min-w-56'>
        <SelectField
          id='site-type-template'
          label='Шаблон ведомости'
          value={шаблон}
          onChange={setШаблон}
          options={(шаблоны.data?.items ?? []).map((строка) => ({
            value: строка.id,
            label: строка.title,
          }))}
          noneLabel='Без шаблона'
          placeholder='Выберите шаблон'
          pending={шаблоны.isPending}
          failed={шаблоны.isError}
        />
      </div>
      <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
        {сохранить.isPending ? 'Заводим…' : 'Завести тип объекта'}
      </Button.Root>
      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
    </form>
  );
}

function СтрокаОбъекта({
  тип,
  можно,
  шаблон,
}: {
  тип: SiteTypeOut;
  можно: boolean;
  шаблон: string | null;
}) {
  const сохранить = useSaveSiteType();
  const [название, setНазвание] = useState(тип.title);
  const [правим, setПравим] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <li className='flex flex-wrap items-center justify-between gap-3 py-2.5'>
      {правим ? (
        // Переименование НЕ трогает ни машинный ключ, ни шаблон ведомости: состав денежных
        // колонок у объектов этого типа от правки названия не поедет.
        <div className='flex min-w-0 flex-1 flex-wrap items-end gap-2'>
          <div className='min-w-56 flex-1'>
            <TextField
              id={`site-type-title-${тип.id}`}
              label='Название'
              value={название}
              onChange={setНазвание}
              hint='По нему тип выбирают в карточке объекта. Шаблон ведомости от переименования не меняется.'
            />
          </div>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => {
              if (!можно) {
                setЗапрет('Переименовывать типы может администратор.');
                return;
              }
              if (название.trim() === '') {
                setЗапрет('Название обязательно: по нему тип выбирают в карточке объекта.');
                return;
              }
              setЗапрет(null);
              сохранить.mutate(
                { id: тип.id, body: { title: название.trim() } },
                { onSuccess: () => setПравим(false) },
              );
            }}
          >
            {сохранить.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              setНазвание(тип.title);
              setЗапрет(null);
              setПравим(false);
            }}
          >
            Отмена
          </Button.Root>
        </div>
      ) : (
        <>
          <div className='flex min-w-0 flex-col'>
            <span className='text-paragraph-sm text-text-strong-950'>
              {тип.title}
              {!тип.is_active && <span className='text-text-sub-600'> · отключён</span>}
            </span>
            {/* Шаблон назван словом, а не опознавателем: человек выбирает тип объекта именно по
                тому, какие колонки будут в ведомости. */}
            <span className='text-paragraph-xs text-text-sub-600'>
              {шаблон ?? 'шаблон не назначен: объекты этого типа возьмут шаблон по умолчанию'}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              onClick={() => setПравим(true)}
            >
              Переименовать
            </Button.Root>
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => {
                if (!можно) {
                  setЗапрет('Отключать типы может администратор.');
                  return;
                }
                setЗапрет(null);
                сохранить.mutate({ id: тип.id, body: { is_active: !тип.is_active } });
              }}
            >
              {тип.is_active ? 'Отключить' : 'Включить'}
            </Button.Root>
          </div>
        </>
      )}
      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
    </li>
  );
}
